<script setup lang="ts">
import { useBreakpoints } from '@vueuse/core';
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';

interface Props {
  /** 闪烁次数 */
  flashCount?: number;
  /** 每次闪烁间隔(ms) */
  flashInterval?: number;
  /** 初始化时是否闪烁 */
  flashOnInit?: boolean;
  /** 额外的CSS类 */
  class?: string;
}

const props = withDefaults(defineProps<Props>(), {
  flashCount: 5,
  flashInterval: 200,
  flashOnInit: true,
  class: '',
});

// Breakpoint detection
const breakpoints = useBreakpoints({ mobile: 0, tablet: 768 });
const isMobile = breakpoints.smaller('tablet');

// Computed class that handles mobile override
const containerClass = computed(() => {
  if (isMobile.value) {
    // On mobile, remove overflow and height constraints but keep spacing/padding
    return props.class
      .replace(/overflow-y-auto/g, 'overflow-visible')
      .replace(/h-\[calc\([^\]]+\)\]/g, 'h-auto');
  }
  return props.class;
});

const containerRef = ref<HTMLElement>();
const isFlashingTop = ref(false);
const isFlashingBottom = ref(false);
const isScrollable = ref(false);
const showTopFade = ref(false); // 控制顶部渐变显示，初始为false
const showBottomFade = ref(false); // 控制底部渐变显示，初始为false，等检测后再设置
const observer = ref<IntersectionObserver>();
const topSentinel = ref<HTMLElement>();
const bottomSentinel = ref<HTMLElement>();
const isScrolling = ref(false); // 跟踪是否在滚动中
const scrollTimer = ref<number>(); // 滚动计时器

/**
 * 检测内容是否可滚动
 */
function checkScrollable() {
  if (!containerRef.value || isMobile.value) {return false;}

  const { scrollHeight, clientHeight } = containerRef.value;
  const scrollable = scrollHeight > clientHeight + 5; // 5px 容差
  isScrollable.value = scrollable;
  return scrollable;
}

/**
 * 创建闪烁效果
 * @param position - 闪烁位置 ('top' | 'bottom')
 */
function createFlash(position: 'top' | 'bottom') {
  // 只有可滚动时才显示闪烁
  if (!checkScrollable()) {return;}

  const flashRef = position === 'top' ? isFlashingTop : isFlashingBottom;
  if (flashRef.value) {return;} // 防止重复闪烁

  let flashCounter = 0;
  const maxFlashes = props.flashCount * 2; // 开关切换

  const flashTimer = setInterval(() => {
    flashRef.value = !flashRef.value;
    flashCounter++;

    if (flashCounter >= maxFlashes) {
      clearInterval(flashTimer);
      flashRef.value = false;
    }
  }, props.flashInterval);
}

/**
 * 检查滚动位置并更新渐变显示
 */
function updateFadeBasedOnScroll() {
  if (!containerRef.value || !checkScrollable()) {
    return;
  }

  const { scrollTop, scrollHeight, clientHeight } = containerRef.value;
  const isAtTop = scrollTop <= 5; // 更宽松的顶部检测
  const isAtBottom = Math.abs(scrollHeight - clientHeight - scrollTop) <= 5;

  // 只有在中间滚动时才设置滚动状态，到达边界时立即显示呼吸灯
  if (isAtTop || isAtBottom) {
    // 到达边界，立即显示呼吸灯
    isScrolling.value = false;
    clearTimeout(scrollTimer.value);
  } else {
    // 在中间滚动，设置滚动状态
    isScrolling.value = true;
    clearTimeout(scrollTimer.value);
    scrollTimer.value = setTimeout(() => {
      isScrolling.value = false;
    }, 3000) as unknown as number;
  }

  if (isAtTop) {
    showTopFade.value = false; // 在顶部，不显示上方模糊
    showBottomFade.value = true; // 显示下方模糊
  } else if (isAtBottom) {
    showTopFade.value = true; // 显示上方模糊
    showBottomFade.value = false; // 在底部，不显示下方模糊
  } else {
    showTopFade.value = true; // 中间时都显示
    showBottomFade.value = true;
  }
}

/**
 * 设置Intersection Observer
 */
function setupObserver() {
  if (!containerRef.value || observer.value) {return;}

  // 创建哨兵元素
  topSentinel.value = document.createElement('div');
  bottomSentinel.value = document.createElement('div');

  // 设置哨兵样式
  const sentinelStyle = {
    position: 'absolute',
    width: '1px',
    height: '1px',
    opacity: '0',
    pointerEvents: 'none',
  };

  Object.assign(topSentinel.value.style, sentinelStyle, { top: '0', left: '0' });
  Object.assign(bottomSentinel.value.style, sentinelStyle, { bottom: '0', left: '0' });

  topSentinel.value.setAttribute('data-sentinel', 'top');
  bottomSentinel.value.setAttribute('data-sentinel', 'bottom');

  // 添加到容器
  containerRef.value.appendChild(topSentinel.value);
  containerRef.value.appendChild(bottomSentinel.value);

  // 创建观察器
  observer.value = new IntersectionObserver(
    entries => {
      if (!checkScrollable()) {return;}

      let atTop = false;
      let atBottom = false;

      entries.forEach(entry => {
        const sentinelType = entry.target.getAttribute('data-sentinel');

        if (sentinelType === 'top' && entry.isIntersecting) {
          atTop = true;
          // 滚动到顶部时，底部闪烁（提示可以向下滚动）
          createFlash('bottom');
        } else if (sentinelType === 'bottom' && entry.isIntersecting) {
          atBottom = true;
          // 滚动到底部时，顶部闪烁（提示可以向上滚动）
          createFlash('top');
        }
      });

      // 更新渐变显示状态
      // 在顶部时：只显示底部渐变（下面还有内容）
      // 在底部时：只显示顶部渐变（上面还有内容）
      // 在中间时：上下都显示（上下都有内容被遮挡）
      if (atTop) {
        showTopFade.value = false; // 上面没有内容，不需要上方模糊
        showBottomFade.value = true; // 下面有内容，显示下方模糊
        // 到达顶部时立即显示呼吸灯
        isScrolling.value = false;
        clearTimeout(scrollTimer.value);
      } else if (atBottom) {
        showTopFade.value = true; // 上面有内容，显示上方模糊
        showBottomFade.value = false; // 下面没有内容，不需要下方模糊
        // 到达底部时立即显示呼吸灯
        isScrolling.value = false;
        clearTimeout(scrollTimer.value);
      } else {
        // 在中间滚动时，上下都有被遮挡的内容，都显示
        showTopFade.value = true;
        showBottomFade.value = true;
      }
    },
    {
      root: containerRef.value,
      rootMargin: '0px',
      threshold: 0,
    },
  );

  observer.value.observe(topSentinel.value);
  observer.value.observe(bottomSentinel.value);

  // 同时添加滚动监听器作为备用
  containerRef.value.addEventListener('scroll', updateFadeBasedOnScroll);
}

/**
 * 清理资源
 */
function cleanup() {
  if (observer.value) {
    observer.value.disconnect();
    observer.value = undefined;
  }

  if (containerRef.value) {
    containerRef.value.removeEventListener('scroll', updateFadeBasedOnScroll);
  }

  if (scrollTimer.value) {
    clearTimeout(scrollTimer.value);
  }

  if (topSentinel.value?.parentNode) {
    topSentinel.value.parentNode.removeChild(topSentinel.value);
  }

  if (bottomSentinel.value?.parentNode) {
    bottomSentinel.value.parentNode.removeChild(bottomSentinel.value);
  }
}

// 监听容器内容变化，重新检测是否可滚动
watch(
  () => containerRef.value,
  () => {
    nextTick(() => {
      checkScrollable();
      setupObserver();
    });
  },
  { flush: 'post' },
);

onMounted(() => {
  nextTick(() => {
    checkScrollable();

    // 初始状态：页面开始时一定在顶部
    if (isScrollable.value) {
      console.log('Initial setup: setting showTopFade=false, showBottomFade=true');
      showTopFade.value = false; // 页面开始时，上面没有内容
      showBottomFade.value = true; // 下面有内容需要滚动查看
      isScrolling.value = false; // 初始状态为非滚动，让呼吸灯立即开始

      // 稍后再设置观察器和滚动监听
      setTimeout(() => {
        setupObserver();
        // 再次检查滚动位置（作为双保险）
        updateFadeBasedOnScroll();
      }, 100);

      // 初始闪烁：底部闪烁提示可以向下滚动
      if (props.flashOnInit) {
        setTimeout(() => {
          createFlash('bottom');
        }, 300);
      }
    } else {
      // 如果不可滚动，都不显示
      showTopFade.value = false;
      showBottomFade.value = false;
    }
  });
});

onUnmounted(() => {
  cleanup();
});

// 暴露containerRef给父组件使用
defineExpose({
  containerRef,
});
</script>

<template>
  <div class="relative">
    <!-- 滚动容器 -->
    <div ref="containerRef" class="hide-scrollbar" :class="containerClass">
      <!-- 内容槽 -->
      <slot />
    </div>

    <!-- 移除模糊效果，改用纯呼吸灯提示 -->

    <!-- 上界背景模糊层 - 常驻，不跟呼吸灯闪烁 -->
    <div
      v-if="isScrollable && showTopFade"
      class="absolute top-2 -left-2 -right-2 h-8 pointer-events-none z-15"
    >
      <div class="absolute inset-0 mystical-top-backdrop" />
      <!-- 向上箭头 - 常驻在模糊层 -->
      <div class="absolute top-2 left-1/2 transform -translate-x-1/2 static-arrow-up z-10">
        <svg
          width="12"
          height="8"
          viewBox="0 0 12 8"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <!-- 白色描边 -->
          <path
            d="M1 7L6 2L11 7"
            stroke="white"
            stroke-width="2.5"
            stroke-linecap="round"
            stroke-linejoin="round"
            opacity="0.8"
          />
          <!-- 彩色箭头 -->
          <path
            d="M1 7L6 2L11 7"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </div>
    </div>

    <!-- 上界神秘光芒 - 呼吸灯效果 -->
    <div
      v-if="isScrollable && showTopFade && !isScrolling && !isFlashingTop"
      class="absolute top-6 -left-2 -right-2 h-8 pointer-events-none z-20 transition-all duration-500"
    >
      <!-- 主题色背景层 -->
      <div class="absolute inset-0 mystical-top-background" />
      <!-- 蓝色呼吸光芒层 - 不模糊 -->
      <div class="absolute inset-0 mystical-top-glow" />
    </div>

    <!-- 下界背景模糊层 - 常驻，不跟呼吸灯闪烁 -->
    <div
      v-if="isScrollable && showBottomFade"
      class="absolute bottom-2 -left-2 -right-2 h-8 pointer-events-none z-15"
    >
      <div class="absolute inset-0 mystical-bottom-backdrop" />
      <!-- 向下箭头 - 常驻在模糊层 -->
      <div class="absolute bottom-6 left-1/2 transform -translate-x-1/2 static-arrow-down z-10">
        <svg
          width="12"
          height="8"
          viewBox="0 0 12 8"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <!-- 白色描边 -->
          <path
            d="M1 1L6 6L11 1"
            stroke="white"
            stroke-width="2.5"
            stroke-linecap="round"
            stroke-linejoin="round"
            opacity="0.8"
          />
          <!-- 彩色箭头 -->
          <path
            d="M1 1L6 6L11 1"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </div>
    </div>

    <!-- 下界神秘光芒 - 呼吸灯效果 -->
    <div
      v-if="isScrollable && showBottomFade && !isScrolling && !isFlashingBottom"
      class="absolute -bottom-4 -left-2 -right-2 h-8 pointer-events-none z-20 transition-all duration-500"
    >
      <!-- 主题色背景层 -->
      <div class="absolute inset-0 mystical-bottom-background" />
      <!-- 蓝色呼吸光芒层 - 不模糊 -->
      <div class="absolute inset-0 mystical-bottom-glow" />
    </div>

    <!-- 顶部边缘光晕 - 只在闪烁时显示 -->
    <div
      v-if="isScrollable && isFlashingTop"
      class="absolute top-0 left-0 right-0 h-px pointer-events-none z-20 transition-all duration-200 bg-gradient-to-r from-transparent via-primary/80 to-transparent shadow-sm shadow-primary/50"
    />

    <!-- 底部边缘光晕 - 只在闪烁时显示 -->
    <div
      v-if="isScrollable && isFlashingBottom"
      class="absolute bottom-0 left-0 right-0 h-px pointer-events-none z-20 transition-all duration-200 bg-gradient-to-r from-transparent via-primary/80 to-transparent shadow-sm shadow-primary/50"
    />
  </div>
</template>

<style scoped>
.hide-scrollbar {
  /* Firefox */
  scrollbar-width: none;

  /* IE and Edge */
  -ms-overflow-style: none;
}

/* WebKit browsers (Chrome, Safari, newer versions of Edge) */
.hide-scrollbar::-webkit-scrollbar {
  display: none;
}

/* 优雅的脉冲光晕动画 */
@keyframes pulse-glow {
  0%,
  100% {
    opacity: 0.3;
    transform: translateX(-50%) scaleY(0.8);
  }

  50% {
    opacity: 0.8;
    transform: translateX(-50%) scaleY(1);
  }
}

/* 优雅的光线闪烁动画 */
@keyframes shimmer {
  0%,
  100% {
    opacity: 0.4;
    transform: scaleX(0.8);
  }

  50% {
    opacity: 1;
    transform: scaleX(1);
  }
}

/* 上界高级渐变背景层 - 毛玻璃效果 */
.mystical-top-background {
  position: relative;
  background: radial-gradient(
    ellipse 120% 80% at center top,
    rgba(255, 255, 255, 0.2) 0%,
    rgba(255, 255, 255, 0.15) 50%,
    rgba(255, 255, 255, 0.08) 80%,
    transparent 100%
  );
  backdrop-filter: blur(25px);
  -webkit-backdrop-filter: blur(25px);
  border-radius: 12px;
}

/* 上界背景模糊层 - 毛玻璃效果 */
.mystical-top-backdrop {
  position: relative;
  background-color: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-radius: 12px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
}

/* 上界高级神秘光芒 - 极简优雅设计 */
.mystical-top-glow {
  position: relative;
  overflow: hidden;
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
}

.mystical-top-glow::before {
  content: '';
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 60%;
  height: 100%;
  background: linear-gradient(
    to bottom,
    rgba(99, 102, 241, 0.15) 0%,
    /* 淡紫蓝 - indigo-400 */ rgba(99, 102, 241, 0.08) 50%,
    transparent 100%
  );
  animation: pulse-glow 3s ease-in-out infinite;
}

/* 移除::after线条效果 */

/* 下界高级渐变背景层 - 毛玻璃效果 */
.mystical-bottom-background {
  position: relative;
  background: radial-gradient(
    ellipse 120% 80% at center bottom,
    rgba(255, 255, 255, 0.2) 0%,
    rgba(255, 255, 255, 0.15) 50%,
    rgba(255, 255, 255, 0.08) 80%,
    transparent 100%
  );
  backdrop-filter: blur(25px);
  -webkit-backdrop-filter: blur(25px);
  border-radius: 12px;
}

/* 下界背景模糊层 - 毛玻璃效果 */
.mystical-bottom-backdrop {
  position: relative;
  background-color: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-radius: 12px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
}

/* 下界高级神秘光芒 - 极简优雅设计 */
.mystical-bottom-glow {
  position: relative;
  overflow: hidden;
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
}

.mystical-bottom-glow::before {
  content: '';
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 60%;
  height: 100%;
  background: linear-gradient(
    to top,
    rgba(99, 102, 241, 0.15) 0%,
    /* 淡紫蓝 - indigo-400 */ rgba(99, 102, 241, 0.08) 50%,
    transparent 100%
  );
  animation: pulse-glow 3s ease-in-out infinite;
}

/* 移除::after线条效果 */

/* 呼吸箭头动画 */
@keyframes arrow-breathing {
  0%,
  100% {
    opacity: 0.6;
    transform: translateY(0px);
  }

  50% {
    opacity: 1;
    transform: translateY(1px);
  }
}

/* 向上箭头 - 黑色 */
.static-arrow-up {
  color: rgba(0, 0, 0, 0.8);
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.2));
  animation: gentle-float-up 2s ease-in-out infinite;
}

/* 向上浮动动画 */
@keyframes gentle-float-up {
  0%,
  100% {
    transform: translateX(-50%) translateY(0);
  }

  50% {
    transform: translateX(-50%) translateY(-6px);
  }
}

/* 向下箭头 - 黑色 */
.static-arrow-down {
  color: rgba(0, 0, 0, 0.8);
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.2));
  animation: gentle-float-down 2s ease-in-out infinite;
}

/* 向下浮动动画 */
@keyframes gentle-float-down {
  0%,
  100% {
    transform: translateX(-50%) translateY(0);
  }

  50% {
    transform: translateX(-50%) translateY(6px);
  }
}
</style>
